/**
 * crossModuleResolver.js
 *
 * Client-side cross-module intelligence.
 * Uses data already fetched by existing tools — no additional DB queries.
 *
 * Answers relational questions by intersecting data from multiple modules:
 * - People needing followup AND in churches with active prayers
 * - Churches under pressure (low activity + high prayer load)
 * - Visit-prayer correlations
 */

/**
 * Find people who need followup AND belong to churches with active prayers.
 * Uses church_id correlation since person→prayer direct join isn't in the adapter fields.
 *
 * @param {Object[]} followupPeople  — rows from get_people_needing_followup
 * @param {Object[]} prayerRows      — rows from get_recent_prayers
 * @returns {{ count: number, insight: string|null }}
 */
export function resolvePeopleAtRisk(followupPeople, prayerRows) {
  if (!followupPeople?.length || !prayerRows?.length) return { count: 0, insight: null };

  const activePrayers    = prayerRows.filter((p) => p.status !== "respondida");
  if (!activePrayers.length) return { count: 0, insight: null };

  const prayerChurchIds = new Set(activePrayers.map((p) => p.church_id).filter(Boolean));
  const atRisk = followupPeople.filter(
    (p) => p.church_id && prayerChurchIds.has(p.church_id)
  );

  if (!atRisk.length) return { count: 0, insight: null };

  const names = atRisk.slice(0, 2).map((p) => p.name).filter(Boolean).join(", ");
  return {
    count:   atRisk.length,
    insight: `${atRisk.length} persona${atRisk.length !== 1 ? "s" : ""} sin seguimiento en iglesias con oraciones activas${names ? `: ${names}` : ""}.`,
  };
}

/**
 * Rank churches by combined pressure (low activity + active prayer load).
 *
 * @param {Object[]} churchRows  — rows from get_church_activity_summary
 * @param {Object[]} prayerRows  — rows from get_recent_prayers
 * @returns {{ highPressure: Object[], insight: string|null }}
 */
export function resolveChurchesByPressure(churchRows, prayerRows) {
  if (!churchRows?.length) return { highPressure: [], insight: null };

  const daysSince = (d) => {
    if (!d) return 999;
    const diff = Date.now() - new Date(d).getTime();
    return isNaN(diff) ? 999 : Math.floor(diff / 86_400_000);
  };

  const prayerCountByChurch = {};
  for (const p of prayerRows ?? []) {
    if (p.church_id && p.status !== "respondida") {
      prayerCountByChurch[p.church_id] = (prayerCountByChurch[p.church_id] ?? 0) + 1;
    }
  }

  const scored = churchRows.map((c) => ({
    ...c,
    prayerCount:  prayerCountByChurch[c.id] ?? 0,
    inactiveDays: daysSince(c.last_activity_date),
  }));

  const highPressure = scored
    .filter((c) => c.prayerCount > 0 && c.inactiveDays > 14)
    .sort((a, b) => (b.prayerCount + b.inactiveDays * 0.05) - (a.prayerCount + a.inactiveDays * 0.05));

  if (!highPressure.length) return { highPressure: [], insight: null };

  const names = highPressure.slice(0, 2).map((c) => c.name).filter(Boolean).join(", ");
  const n     = highPressure.length;
  return {
    highPressure,
    insight: `${n} iglesia${n !== 1 ? "s" : ""} con oraciones activas y poca actividad reciente${names ? `: ${names}` : ""}.`,
  };
}

/**
 * Build a cross-module intelligence context from available tool data.
 * Returns null if no meaningful correlations found.
 *
 * @param {Record<string, Object[]>} dataByTool
 * @returns {{ insight: string|null, atRiskCount: number }}
 */
export function resolveCrossModuleContext(dataByTool) {
  const followupPeople = dataByTool["get_people_needing_followup"] ?? [];
  const prayerRows     = dataByTool["get_recent_prayers"]           ?? [];
  const churchRows     = dataByTool["get_church_activity_summary"]  ?? [];

  const atRisk     = resolvePeopleAtRisk(followupPeople, prayerRows);
  const byPressure = resolveChurchesByPressure(churchRows, prayerRows);

  const insights = [atRisk.insight, byPressure.insight].filter(Boolean);

  return {
    insight:     insights.join(" ") || null,
    atRiskCount: atRisk.count,
  };
}
